import { buildHurricaneResult, isHurricaneFeature } from './HurricaneResult';
import {
  formatAttributeValue,
  formatFeatureCount,
  formatFigure,
  NO_DATA_MESSAGE,
  NO_FEATURES_MESSAGE,
  toRowLocation,
  type LayerAnalyticsCard,
  type LayerAnalyticsContent,
  type LayerAnalyticsFeature,
  type LayerAnalyticsResult,
  type LayerAnalyticsTable,
} from './layerAnalytics.utils';
import {
  buildWildfireResult,
  isWildfireIncidentFeature,
} from './WildfireResult';

const TITLE_FIELD_CANDIDATES = [
  'name',
  'NAME',
  'title',
  'STORMNAME',
  'IncidentName',
  'event',
] as const;
const MAX_ATTRIBUTE_COLUMNS = 6;
const SKIPPED_FIELD_PATTERN = /^(objectid|shape.*|geometry)$/i;

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Generic template (one card, table only): a "Title" column plus up to six
 * attribute columns, skipping OBJECTID / Shape / geometry and non-primitive
 * fields.
 */
export const buildGenericTable = (
  features: LayerAnalyticsFeature[]
): LayerAnalyticsTable => {
  const fieldNames: string[] = [];
  features.forEach(({ attributes }) => {
    Object.entries(attributes).forEach(([key, value]) => {
      if (
        !fieldNames.includes(key) &&
        formatAttributeValue(value) !== undefined
      ) {
        fieldNames.push(key);
      }
    });
  });

  const titleField = TITLE_FIELD_CANDIDATES.find((candidate) =>
    fieldNames.includes(candidate)
  );
  const attributeFields = fieldNames
    .filter((key) => key !== titleField && !SKIPPED_FIELD_PATTERN.test(key))
    .slice(0, MAX_ATTRIBUTE_COLUMNS);

  return {
    title: null,
    nameLabel: 'Title',
    columns: attributeFields.map((key) => ({ key, label: key })),
    rows: features.map((feature, index) => ({
      name:
        (titleField && formatAttributeValue(feature.attributes[titleField])) ||
        `Feature ${index + 1}`,
      cells: Object.fromEntries(
        attributeFields.map((key) => [
          key,
          formatAttributeValue(feature.attributes[key]) ?? '',
        ])
      ),
      ...toRowLocation(feature),
    })),
  };
};

/**
 * Builds the result for one layer: the wildfire or hurricane template when the
 * features match one, otherwise the generic attribute table.
 */
export const buildLayerAnalyticsResult = ({
  layerValue,
  label,
  features,
}: {
  layerValue: string;
  label: string;
  features: LayerAnalyticsFeature[];
}): LayerAnalyticsResult => {
  if (features.some(isWildfireIncidentFeature)) {
    return buildWildfireResult({ layerValue, label, features });
  }
  if (features.some(isHurricaneFeature)) {
    return buildHurricaneResult({ layerValue, label, features });
  }
  return {
    layerValue,
    label,
    hazard: null,
    featureCount: features.length,
    cards:
      features.length === 0
        ? []
        : [
            {
              key: 'features',
              title: null,
              figures: [],
              charts: [],
              table: buildGenericTable(features),
            },
          ],
  };
};

const tableToHtml = ({
  title,
  nameLabel,
  columns,
  rows,
}: LayerAnalyticsTable) => {
  const heading = title ? `<h5>${escapeHtml(title)}</h5>` : '';
  if (rows.length === 0) return `${heading}<p>${NO_FEATURES_MESSAGE}</p>`;
  const head = [nameLabel, ...columns.map((column) => column.label)]
    .map((label) => `<th>${escapeHtml(label)}</th>`)
    .join('');
  const body = rows
    .map(
      (row) =>
        `<tr>${[
          row.name,
          ...columns.map((column) => row.cells[column.key] ?? ''),
        ]
          .map((cell) => `<td>${escapeHtml(cell)}</td>`)
          .join('')}</tr>`
    )
    .join('');
  return `${heading}<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
};

const cardToHtml = ({ title, figures, charts, table }: LayerAnalyticsCard) => {
  const heading = title ? `<h5>${escapeHtml(title)}</h5>` : '';
  const figureText = figures
    .map((figure) => `${escapeHtml(figure.label)}: ${formatFigure(figure)}`)
    .join(' | ');
  const chartText = charts
    .map((chart) => {
      const total = chart.slices.reduce((sum, { count }) => sum + count, 0);
      const slices = chart.slices
        .filter(({ count }) => count > 0)
        .map(
          ({ label, count }) =>
            `${escapeHtml(label)} ${count} (${Math.round((count / total) * 100)}%)`
        )
        .join(', ');
      return `<p>${escapeHtml(chart.title)}: ${slices || NO_DATA_MESSAGE}</p>`;
    })
    .join('');
  return (
    heading +
    (figureText ? `<p>${figureText}</p>` : '') +
    chartText +
    (table ? tableToHtml(table) : '')
  );
};

const resultToHtml = ({ label, featureCount, cards }: LayerAnalyticsResult) => {
  const heading = `<h4>${escapeHtml(label)} (${formatFeatureCount(featureCount)})</h4>`;
  if (cards.length === 0) return `${heading}<p>${NO_FEATURES_MESSAGE}</p>`;
  return heading + cards.map(cardToHtml).join('');
};

/**
 * Plain HTML rendering of the content (headings, figure lines, chart summaries
 * and tables) saved as `sitrep_section_text` for exports and AI consumers; the
 * structured content itself is saved as `section_data`.
 */
export const serializeLayerAnalyticsContent = (
  content: LayerAnalyticsContent
): string => content.results.map(resultToHtml).join('');
